// EPSE Statistics Calculations
import { ProfileStatistics, EPSEQuarterData } from '@/types/epse';

/**
 * Calculate percentile value from sorted array
 */
function percentile(sortedValues: number[], p: number): number {
  if (sortedValues.length === 0) return 0;
  const index = (p / 100) * (sortedValues.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sortedValues[lower];
  return sortedValues[lower] + (sortedValues[upper] - sortedValues[lower]) * (index - lower);
}

/**
 * Calculate standard deviation
 */
function standardDeviation(values: number[], mean: number): number {
  if (values.length === 0) return 0;
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  return Math.sqrt(avgSquaredDiff);
}

/**
 * Calculate complete profile statistics from quarter data
 */
export function calculateProfileStatistics(quarterData: EPSEQuarterData[]): ProfileStatistics {
  if (quarterData.length === 0) {
    return {
      p50: 0,
      p95: 0,
      p99: 0,
      max: 0,
      mean: 0,
      std_dev: 0,
      ramp_rate_avg: 0,
      ramp_rate_max: 0,
      night_base_p10: 0,
      total_kwh: 0,
      peak_hours: [],
    };
  }
  
  const values = quarterData.map(q => q.kw_value);
  const sorted = [...values].sort((a, b) => a - b);
  
  // Basic statistics
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const max = Math.max(...values);
  const p50 = percentile(sorted, 50);
  const p95 = percentile(sorted, 95);
  const p99 = percentile(sorted, 99);
  const std_dev = standardDeviation(values, mean);
  
  // Total energy (kWh = kW * 0.25 hours per quarter)
  const total_kwh = values.reduce((sum, kw) => sum + kw * 0.25, 0);
  
  // Ramp rates (ΔkW per 15 minutes)
  const rampRates: number[] = [];
  for (let i = 1; i < values.length; i++) {
    rampRates.push(Math.abs(values[i] - values[i - 1]));
  }
  const ramp_rate_avg = rampRates.length > 0 
    ? rampRates.reduce((a, b) => a + b, 0) / rampRates.length 
    : 0;
  const ramp_rate_max = rampRates.length > 0 ? Math.max(...rampRates) : 0;
  
  // Night base (P10 of night hours: 22:00-06:00)
  const nightValues = quarterData
    .filter(q => q.time_context?.is_night)
    .map(q => q.kw_value);
  const night_base_p10 = nightValues.length > 0 
    ? percentile([...nightValues].sort((a, b) => a - b), 10) 
    : 0;
  
  // Peak hours (hours where average kW > P95)
  const hourlyAverages: Map<number, number[]> = new Map();
  quarterData.forEach(q => {
    const hour = q.time_context?.hour_of_day ?? new Date(q.start_time).getHours();
    if (!hourlyAverages.has(hour)) hourlyAverages.set(hour, []);
    hourlyAverages.get(hour)!.push(q.kw_value);
  });
  
  const peak_hours: number[] = [];
  hourlyAverages.forEach((vals, hour) => {
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    if (avg > p95) peak_hours.push(hour);
  });
  
  return {
    p50,
    p95,
    p99,
    max,
    mean,
    std_dev,
    ramp_rate_avg,
    ramp_rate_max,
    night_base_p10,
    total_kwh,
    peak_hours: peak_hours.sort((a, b) => a - b),
  };
}

/**
 * Calculate exceedance statistics above connection limit
 */
export function calculateExceedanceStats(
  quarterData: EPSEQuarterData[],
  connectionLimit: number
): {
  count: number;
  duration_hours: number;
  energy_above_limit_kwh: number;
  max_exceedance_kw: number;
  exceedances: Array<{ timestamp: string; kw: number; exceedance: number }>;
} {
  const exceedances: Array<{ timestamp: string; kw: number; exceedance: number }> = [];
  let energyAbove = 0;
  let maxExceedance = 0;
  
  quarterData.forEach(q => {
    if (q.kw_value > connectionLimit) {
      const exceedance = q.kw_value - connectionLimit;
      exceedances.push({
        timestamp: q.start_time,
        kw: q.kw_value,
        exceedance,
      });
      energyAbove += exceedance * 0.25; // kWh
      maxExceedance = Math.max(maxExceedance, exceedance);
    }
  });
  
  return {
    count: exceedances.length,
    duration_hours: exceedances.length * 0.25,
    energy_above_limit_kwh: energyAbove,
    max_exceedance_kw: maxExceedance,
    exceedances,
  };
}

/**
 * Build normalized shape (96 values representing average kW per quarter of day)
 */
export function buildNormalizedShape(quarterData: EPSEQuarterData[]): number[] {
  const quarterBuckets: number[][] = Array.from({ length: 96 }, () => []);
  
  quarterData.forEach(q => {
    const quarterIndex = q.time_context?.quarter_of_day ?? 
      Math.floor(new Date(q.start_time).getHours() * 4 + new Date(q.start_time).getMinutes() / 15);
    if (quarterIndex >= 0 && quarterIndex < 96) {
      quarterBuckets[quarterIndex].push(q.kw_value);
    }
  });
  
  return quarterBuckets.map(bucket => 
    bucket.length > 0 ? bucket.reduce((a, b) => a + b, 0) / bucket.length : 0
  );
}

/**
 * Build duration curve (sorted kW values from highest to lowest)
 */
export function buildDurationCurve(quarterData: EPSEQuarterData[]): number[] {
  return quarterData
    .map(q => q.kw_value)
    .sort((a, b) => b - a);
}

/**
 * Group quarter data by day type and season
 */
export function groupByContext(quarterData: EPSEQuarterData[]): Map<string, EPSEQuarterData[]> {
  const groups = new Map<string, EPSEQuarterData[]>();
  
  quarterData.forEach(q => {
    const key = `${q.time_context?.day_type || 'unknown'}_${q.time_context?.season || 'unknown'}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(q);
  });
  
  return groups;
}

/**
 * Calculate monthly peak statistics
 */
export function calculateMonthlyPeaks(quarterData: EPSEQuarterData[]): Array<{
  month: number;
  year: number;
  peak_kw: number;
  avg_kw: number;
  total_kwh: number;
}> {
  const monthlyData = new Map<string, EPSEQuarterData[]>();
  
  quarterData.forEach(q => {
    const date = new Date(q.start_time);
    const key = `${date.getFullYear()}-${date.getMonth() + 1}`;
    if (!monthlyData.has(key)) monthlyData.set(key, []);
    monthlyData.get(key)!.push(q);
  });
  
  return Array.from(monthlyData.entries()).map(([key, data]) => {
    const [year, month] = key.split('-').map(Number);
    const values = data.map(q => q.kw_value);
    return {
      month,
      year,
      peak_kw: Math.max(...values),
      avg_kw: values.reduce((a, b) => a + b, 0) / values.length,
      total_kwh: values.reduce((sum, kw) => sum + kw * 0.25, 0),
    };
  }).sort((a, b) => a.year - b.year || a.month - b.month);
}
